import { useNavigate } from "react-router-dom";
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { MdOutlineArrowCircleRight, MdOutlineArrowCircleLeft, MdKeyboardArrowDown, MdClose } from "react-icons/md";
import { FaPencil } from "react-icons/fa6";
import { useTrackContext } from "../../hooks/useTrackContext";
import { TSelectedVariants, TTrackTypes } from "../../types";
import { ROUTES } from "../../consts";

interface IBottomSummaryProps {
  previousInfo: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    component?: React.ReactElement;
  };
  nextInfo: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    component?: React.ReactElement;
  };
}

const BottomSummary = ({ previousInfo, nextInfo }: IBottomSummaryProps) => {
  const {
    trackSelectionOption,
    mainTrackSelection,
    battleKartSelection,
    miniTrackSection,
    productRenderedLocation,
    vrSelection,
    juniorTrackSelection,
    headlessCartTotalAmount,
    shopifyCartVariants,
  } = useTrackContext();
  const navigate = useNavigate();
  const [isTotalVisible, setIsTotalVisible] = useState<boolean>(false);
  const [summaryHeight, setSummaryHeight] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const trackName: string = useMemo(() => {
    const { title } = trackSelectionOption;
    return title === "BATTLE_KART_TRACK"
      ? "BattleKart"
      : title === "MINI_TRACK"
      ? "Mini Track"
      : title === "VIRTUAL_REALITY"
      ? "Virtual Reality"
      : title === "JUNIOR_TRACK"
      ? "Junior Track"
      : title === "MAIN_TRACK"
      ? "Main Track"
      : title;
  }, [trackSelectionOption]);

  const selectedTrack: TTrackTypes = useMemo(() => {
    const { title } = trackSelectionOption;
    return title as TTrackTypes;
  }, [trackSelectionOption]);

  const selectedNumberOfPlayers: number = useMemo(() => {
    const { title } = trackSelectionOption;

    if (title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfSeats || 0;
    }

    if (title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfSeats || 0;
    }

    if (title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfSeats || 0;
    }

    if (title === "VIRTUAL_REALITY") {
      return vrSelection.playersandsessions.numberOfPlayers || 0;
    }

    if (title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfSeats || 0;
    }

    return 0;
  }, [
    battleKartSelection.racesSeats.numberOfSeats,
    juniorTrackSelection.racesSeats.numberOfSeats,
    mainTrackSelection.racesSeats.numberOfSeats,
    miniTrackSection.racesSeats.numberOfSeats,
    vrSelection.playersandsessions.numberOfPlayers,
    trackSelectionOption,
  ]);

  const selectedNumberOfSessions: number = useMemo(() => {
    const { title } = trackSelectionOption;

    if (title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfRaces || 0;
    }

    if (title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfRaces || 0;
    }

    if (title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfRaces || 0;
    }

    if (title === "VIRTUAL_REALITY") {
      return vrSelection.playersandsessions.numberOfSessions || 0;
    }

    if (title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfRaces || 0;
    }

    return 0;
  }, [
    battleKartSelection.racesSeats.numberOfRaces,
    juniorTrackSelection.racesSeats.numberOfRaces,
    mainTrackSelection.racesSeats.numberOfRaces,
    miniTrackSection.racesSeats.numberOfRaces,
    vrSelection.playersandsessions.numberOfSessions,
    trackSelectionOption,
  ]);

  const selectedLineItems: TSelectedVariants[] = useMemo(() => {
    const allSelectedVariants: TSelectedVariants[] = [
      ...mainTrackSelection.variantsAndDate.selectedVariants,
      ...battleKartSelection.variantsAndDate.selectedVariants,
      ...miniTrackSection.variantsAndDate.selectedVariants,
      ...vrSelection.variantsAndDate.selectedVariants,
      ...juniorTrackSelection.variantsAndDate.selectedVariants,
    ];

    return allSelectedVariants;
  }, [
    battleKartSelection.variantsAndDate.selectedVariants,
    juniorTrackSelection.variantsAndDate.selectedVariants,
    mainTrackSelection.variantsAndDate.selectedVariants,
    miniTrackSection.variantsAndDate.selectedVariants,
    vrSelection.variantsAndDate.selectedVariants,
  ]);

  const shopifySelectedLineItems: TSelectedVariants[] = useMemo(() => {
    return shopifyCartVariants;
  }, [shopifyCartVariants]);

  const totalAmount: number = useMemo(() => {
    return productRenderedLocation === "external" ? Number(headlessCartTotalAmount) : Number(Number(headlessCartTotalAmount) / 100);
  }, [headlessCartTotalAmount, productRenderedLocation]);

  const toggleTotal = () => {
    setIsTotalVisible(!isTotalVisible);
  };

  const isItMainTrackAgeGroupSelected: boolean = useMemo(() => {
    return mainTrackSelection.ageGroup !== null;
  }, [mainTrackSelection.ageGroup]);

  const isItMainTrackRaceTypeSelected: boolean = useMemo(() => {
    return mainTrackSelection.raceType !== null;
  }, [mainTrackSelection.raceType]);

  const generatedBgColor = useMemo(() => {
    return selectedTrack === "BATTLE_KART_TRACK" ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]";
  }, [selectedTrack]);

  const generateSummaryBgColor = useMemo(() => {
    return selectedTrack === "BATTLE_KART_TRACK" ? "tw-bg-[linear-gradient(180deg,rgba(250,198,24,0.7)_0%,rgba(234,88,26,0.7)_100%)]" : "tw-bg-[linear-gradient(71deg,rgba(168,82,255,0.7)_28.13%,rgba(120,134,255,0.7))]";
  }, [selectedTrack]);

  const generateLineItemBgColor = useCallback((productType: string) => {
    return productType === "BattleKart" ? "tw-bg-[linear-gradient(180deg,rgba(250,198,24,0.7)_0%,rgba(234,88,26,0.7)_100%)]" : "tw-bg-[linear-gradient(71deg,rgba(168,82,255,0.6)_28.13%,rgba(120,134,255,0.6))]";
  }, []);

  const generateNextButtonBgColor = useMemo(() => {
    return selectedTrack === "BATTLE_KART_TRACK" ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]";
  }, [selectedTrack]);

  const handleNavigate = useCallback(
    (route: "EDIT_AGE_GROUP" | "EDIT_RACE_TYPE" | "EDIT_SEATS_RACES_OR_SESSIONS", trackType: TTrackTypes) => {
      if (trackType === "MAIN_TRACK" && route === "EDIT_SEATS_RACES_OR_SESSIONS") {
        navigate("/" + ROUTES.MAIN_TRACK.SEATS_RACES);
      } else if (trackType === "MINI_TRACK" && route === "EDIT_SEATS_RACES_OR_SESSIONS") {
        navigate("/" + ROUTES.MINI_TRACK.SEATS_RACES);
      } else if (trackType === "VIRTUAL_REALITY" && route === "EDIT_SEATS_RACES_OR_SESSIONS") {
        navigate("/" + ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS);
      } else if (trackType === "JUNIOR_TRACK" && route === "EDIT_SEATS_RACES_OR_SESSIONS") {
        navigate("/" + ROUTES.JUNIOR_TRACK.SEATS_RACES);
      } else if (trackType === "BATTLE_KART_TRACK" && route === "EDIT_SEATS_RACES_OR_SESSIONS") {
        navigate("/" + ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS);
      }
    },
    [navigate]
  );

  useEffect(() => {
    const updateHeight = () => {
      if (summaryRef.current && window.innerWidth >= 640 && window.innerWidth <= 768) {
        setSummaryHeight(summaryRef.current.clientHeight);
      } else if (window.innerWidth <= 640) {
        setSummaryHeight(40);
      } else {
        setSummaryHeight(86);
      }
    };

    const updateMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateHeight();
    updateMobile();

    const handleResize = () => {
      updateHeight();
      updateMobile();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [summaryRef.current?.clientHeight]);

  return (
    <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[100]">
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-h-full tw-items-center">
        <div
          className={`tw-flex-1 tw-bg-[#393B41] md:tw-p-4 md:tw-h-[86px] tw-flex tw-items-center tw-justify-center tw-order-2 sm:tw-order-1 tw-w-full sm:tw-w-auto`}
          ref={summaryRef}
        >
          <div className="tw-flex tw-justify-center xl:tw-ml-[350px]">
            <div className="tw-flex tw-flex-row tw-gap-2 tw-py-2">
              <button
                className="tw-bg-black tw-text-white tw-font-[700] tw-px-[20px] tw-py-[8px] lg:tw-py-[18px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-[40px] lg:tw-h-[54px]"
                onClick={() => navigate(previousInfo.pagePath)}
              >
                <MdOutlineArrowCircleLeft className="tw-text-white tw-text-[24px]" />
                <p className="tw-text-[14px] tw-font-[700] tw-text-white tw-uppercase tw-font-[700]">{isMobile ? "BACK" : previousInfo.pageName}</p>
              </button>
              {nextInfo.isActive && nextInfo.component ? (
                <div className={`tw-flex tw-items-center tw-gap-2`}>
                  <button
                    className={`tw-text-white tw-font-[700] tw-px-[20px] tw-py-[8px] lg:tw-py-[18px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-[40px] lg:tw-h-[54px] ${generateNextButtonBgColor}`}
                    onClick={() => navigate(nextInfo.pagePath)}
                  >
                    <p className="tw-text-[14px] tw-font-[700] tw-text-white tw-uppercase tw-font-[700]">
                      {nextInfo.pageName}
                    </p>
                    <MdOutlineArrowCircleRight className="tw-text-white tw-text-[24px]" />
                  </button>
                  {nextInfo.component}
                </div>
              ) : (
                nextInfo.isActive && (
                  <button
                    className={`tw-text-white tw-font-[700] tw-px-[20px] tw-py-[8px] lg:tw-py-[18px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-[40px] lg:tw-h-[54px] ${generateNextButtonBgColor}`}
                    onClick={() => navigate(nextInfo.pagePath)}
                  >
                    <p className="tw-text-[14px] tw-font-[700] tw-text-white tw-uppercase tw-font-[700]">
                      {nextInfo.pageName}
                    </p>
                    <MdOutlineArrowCircleRight className="tw-text-white tw-text-[24px]" />
                  </button>
                )
              )}
              {!nextInfo.isActive && (
                <button
                  className={`tw-text-white tw-font-[700] tw-px-[20px] tw-py-[8px] lg:tw-py-[18px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-[40px] lg:tw-h-[54px] ${generateNextButtonBgColor} tw-cursor-not-allowed tw-opacity-75`}
                >
                  <p className="tw-text-[14px] tw-font-[700] tw-text-white tw-uppercase tw-font-[700]">
                    {nextInfo.pageName}
                  </p>
                  <MdOutlineArrowCircleRight className="tw-text-white tw-text-[24px]" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-justify-start tw-relative tw-order-1 sm:tw-order-2 tw-w-full sm:tw-w-auto tw-h-full">
          <div
            className={`tw-transition-all tw-w-full sm:tw-w-[350px] tw-duration-300 tw-ease-in-out tw-absolute tw-right-0 tw-rounded-tl-[15px] tw-rounded-tr-[10px] tw-overflow-hidden tw-bottom-full tw-bg-[#393B41] tw-shadow-[0_-4px_8px_rgba(0,0,0,0.3),-4px_0_8px_rgba(0,0,0,0.3),4px_0_8px_rgba(0,0,0,0.3)] ${
              isTotalVisible ? "tw-opacity-100 tw-transform tw-translate-y-0" : "tw-opacity-0 tw-transform tw-translate-y-2"
            }`}
          >
            <div className={`tw-flex tw-flex-col tw-gap-2 ${isTotalVisible ? "tw-max-h-[400px]" : "tw-max-h-[0px]"}`}>
              <div className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-3.5 tw-bg-[#393B41] tw-sticky tw-top-0 tw-z-10">
                <p className="tw-text-[14px] tw-font-[700] tw-pl-[10px] tw-text-white">Summary</p>
                <MdClose className="tw-text-white tw-text-[24px] hover:tw-cursor-pointer tw-font-[700]" onClick={toggleTotal} />
              </div>
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-2 tw-px-[10px] tw-py-[10px] tw-overflow-y-auto tw-max-h-[450px] tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-gray-100">
                <div className={`tw-flex tw-items-center tw-justify-between tw-rounded-[6px] ${generateSummaryBgColor} tw-gap-2 tw-w-full tw-mb-1 tw-px-2 tw-py-3`}>
                  <p className="tw-text-[14px] tw-font-medium tw-text-white">Activity</p>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <p className="tw-text-[14px] tw-text-white tw-font-[700]">{trackName}</p>
                    <span className="tw-bg-black tw-text-white tw-text-[14px] hover:tw-cursor-pointer tw-p-[6px] tw-rounded-full" onClick={() => navigate("/")}>
                      <FaPencil />
                    </span>
                  </div>
                </div>

                {selectedTrack === "MAIN_TRACK" && isItMainTrackAgeGroupSelected && (
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                    <div className={`tw-flex tw-items-center tw-justify-between tw-rounded-[6px] ${generateSummaryBgColor} tw-gap-2 tw-w-full tw-mb-1 tw-px-2 tw-py-3`}>
                      <p className="tw-text-[14px] tw-font-medium tw-text-white">Age Group</p>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <p className="tw-text-[14px] tw-text-white tw-font-[700]">{mainTrackSelection.ageGroup}</p>
                        <span
                          className="tw-bg-black tw-text-white tw-text-[14px] hover:tw-cursor-pointer tw-p-[6px] tw-rounded-full"
                          onClick={() => navigate("/" + ROUTES.MAIN_TRACK.AGE_GROUP)}
                        >
                          <FaPencil />
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTrack === "MAIN_TRACK" && isItMainTrackRaceTypeSelected && (
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                    <div className={`tw-flex tw-items-center tw-justify-between tw-rounded-[6px] ${generateSummaryBgColor} tw-gap-2 tw-w-full tw-mb-1 tw-px-2 tw-py-3`}>
                      <p className="tw-text-[14px] tw-font-medium tw-text-white">Race Type</p>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <p className="tw-text-[14px] tw-text-white tw-font-[700]">{mainTrackSelection.raceType}</p>
                        <span
                          className="tw-bg-black tw-text-white tw-text-[14px] hover:tw-cursor-pointer tw-p-[6px] tw-rounded-full"
                          onClick={() => navigate("/" + ROUTES.MAIN_TRACK.RACE_TYPE)}
                        >
                          <FaPencil />
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="tw-flex tw-flex-col tw-w-full tw-gap-2">
                  <div className={`tw-flex tw-items-center tw-justify-between tw-rounded-[6px] ${generateSummaryBgColor} tw-gap-2 tw-w-full tw-mb-1 tw-px-2 tw-py-3`}>
                    <p className="tw-text-[14px] tw-font-medium tw-text-white">Number of Players</p>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <p className="tw-text-[14px] tw-text-white tw-font-[700]">{selectedNumberOfPlayers}</p>
                      <span
                        className="tw-bg-black tw-text-white tw-text-[14px] hover:tw-cursor-pointer tw-p-[6px] tw-rounded-full"
                        onClick={() => handleNavigate("EDIT_SEATS_RACES_OR_SESSIONS", selectedTrack)}
                      >
                        <FaPencil />
                      </span>
                    </div>
                  </div>

                  <div className={`tw-flex tw-items-center tw-justify-between tw-rounded-[6px] ${generateSummaryBgColor} tw-gap-2 tw-w-full tw-mb-1 tw-px-2 tw-py-3`}>
                    <p className="tw-text-[14px] tw-font-medium tw-text-white">Number of Sessions</p>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <p className="tw-text-[14px] tw-text-white tw-font-[700]">{selectedNumberOfSessions}</p>
                      <span
                        className="tw-bg-black tw-text-white tw-text-[14px] hover:tw-cursor-pointer tw-p-[6px] tw-rounded-full"
                        onClick={() => handleNavigate("EDIT_SEATS_RACES_OR_SESSIONS", selectedTrack)}
                      >
                        <FaPencil />
                      </span>
                    </div>
                  </div>
                </div>

                {totalAmount > 0 && (selectedLineItems.length > 0 || shopifySelectedLineItems.length > 0) && (
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                    <p className={`tw-text-[14px] tw-text-left tw-font-[700] tw-text-white tw-my-2`}>Activities</p>
                    {(() => {
                      if (selectedLineItems.length === 0) {
                        return null;
                      }
                      // Group items by productType and title
                      const groupedItems = selectedLineItems.reduce((acc, item) => {
                        const { product } = item.variantsSelectedObject;
                        const { productType, title } = product;
                        const groupKey = `${productType}-${title}`;

                        if (!acc[groupKey]) {
                          acc[groupKey] = {
                            title: title,
                            productType: productType,
                            items: [],
                          };
                        }

                        acc[groupKey].items.push(item);
                        return acc;
                      }, {} as Record<string, { title: string; productType: string; items: TSelectedVariants[] }>);

                      return Object.entries(groupedItems).map(([groupKey, group]) => (
                        <div key={groupKey} className={`tw-flex tw-flex-col tw-gap-1 tw-rounded-[6px] ${generateLineItemBgColor(group.productType)} tw-mb-1`}>
                          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full tw-px-2 tw-pt-3">
                            <p className="tw-text-[14px] tw-font-medium tw-text-white">
                              {group.title.includes("BattleKart")
                                ? group.title.replace("| 10 Minute Session", "")
                                : group.productType === "Main Track"
                                ? group.title + " | " + group.items[0].variantsSelectedObject.title.split("/").slice(0, -1).join("/")
                                : group.title}
                            </p>
                          </div>
                          <div className="tw-grid tw-grid-cols-4 tw-gap-1 tw-pb-2 tw-px-2">
                            {group.items.map((item: TSelectedVariants) => {
                              const { selectedOptions, id } = item.variantsSelectedObject;
                              const selectedOptionsString = selectedOptions.map((option) => (option.name.toLowerCase().includes("time") ? option.value : null)).filter(Boolean);

                              return (
                                <p className="tw-text-[14px] tw-font-medium tw-text-black tw-px-2 tw-py-2 tw-w-full tw-text-center tw-bg-white tw-rounded-lg" key={id}>
                                  {selectedOptionsString.join(" - ")}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}

                    {(() => {
                      if (shopifySelectedLineItems.length === 0) {
                        return null;
                      }

                      // Group items by productType and title
                      const groupedItems = shopifySelectedLineItems.reduce((acc, item) => {
                        const { product } = item.variantsSelectedObject;
                        const { productType, title } = product;
                        const groupKey = `${productType}-${title}`;

                        if (!acc[groupKey]) {
                          acc[groupKey] = {
                            title: title,
                            productType: productType,
                            items: [],
                          };
                        }

                        acc[groupKey].items.push(item);
                        return acc;
                      }, {} as Record<string, { title: string; productType: string; items: TSelectedVariants[] }>);

                      return Object.entries(groupedItems).map(([groupKey, group]) => (
                        <div key={groupKey} className={`tw-flex tw-flex-col tw-gap-1 tw-rounded-[6px] ${generateLineItemBgColor(group.productType)} tw-mb-1`}>
                          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full tw-px-2 tw-pt-3">
                            <p className="tw-text-[14px] tw-font-medium tw-text-white">
                              {group.title.includes("BattleKart")
                                ? group.title.replace("| 10 Minute Session", "")
                                : group.productType === "Main Track"
                                ? group.title + " | " + group.items[0].variantsSelectedObject.title.split("/").slice(0, -1).join("/")
                                : group.title}
                            </p>
                          </div>
                          <div className="tw-grid tw-grid-cols-4 tw-gap-1 tw-pb-2 tw-px-2">
                            {group.items.map((item: TSelectedVariants) => {
                              const { selectedOptions, id } = item.variantsSelectedObject;
                              const selectedOptionsString = selectedOptions.map((option) => (option.name.toLowerCase().includes("time") ? option.value : null)).filter(Boolean);

                              return (
                                <p className="tw-text-[14px] tw-font-medium tw-text-black tw-px-2 tw-py-2 tw-w-full tw-text-center tw-bg-white tw-rounded-lg" key={id}>
                                  {selectedOptionsString.join(" - ")}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={`tw-flex tw-items-center tw-justify-between tw-w-full sm:tw-w-[350px] ${generatedBgColor} tw-px-2`}
            style={{ height: summaryHeight > 0 ? `${summaryHeight}px` : "auto" }}
          >
            <button className="tw-text-white tw-text-[14px] tw-font-[700] tw-py-1 tw-flex tw-items-center tw-gap-1" onClick={toggleTotal}>
              <MdKeyboardArrowDown
                className={`tw-text-white tw-text-[24px] tw-transition-transform tw-duration-[400ms] tw-ease-in-out ${isTotalVisible ? "tw-rotate-0" : "tw-rotate-180"}`}
              />
              {isTotalVisible ? "Hide Summary" : "View Summary"}
            </button>
            {totalAmount > 0 && (
              <p className="tw-text-[14px] tw-font-semibold tw-text-white">Total: ${productRenderedLocation === "external" ? totalAmount.toFixed(0) : totalAmount.toFixed(2)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottomSummary;
